import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DISPLAY_SETTINGS_KEY,
  LANGUAGE_STORAGE_KEY,
  isSupportedLanguage,
  translate,
  type LanguageCode,
} from '../i18n/translations';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (code: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function readStoredLanguage(): LanguageCode {
  try {
    const direct = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (direct && isSupportedLanguage(direct)) return direct;
    const raw = localStorage.getItem(DISPLAY_SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { language?: string };
      if (parsed.language && isSupportedLanguage(parsed.language)) return parsed.language;
    }
  } catch {
    // ignore corrupt storage
  }
  return 'en';
}

function persistLanguage(code: LanguageCode) {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  try {
    const raw = localStorage.getItem(DISPLAY_SETTINGS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    localStorage.setItem(DISPLAY_SETTINGS_KEY, JSON.stringify({ ...parsed, language: code }));
  } catch {
    // ignore
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(readStoredLanguage);

  const setLanguage = useCallback((code: string) => {
    const next = isSupportedLanguage(code) ? code : 'en';
    setLanguageState(next);
    persistLanguage(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY && event.newValue && isSupportedLanguage(event.newValue)) {
        setLanguageState(event.newValue);
      }
      if (event.key === DISPLAY_SETTINGS_KEY && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue) as { language?: string };
          if (parsed.language && isSupportedLanguage(parsed.language)) {
            setLanguageState(parsed.language);
          }
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const t = useCallback((key: string) => translate(language, key), [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: 'en',
      setLanguage: () => undefined,
      t: (key) => key,
    };
  }
  return context;
}
